//src\orbs\OrbResources.ts
import * as THREE from 'three';
import { RenderingPreferences } from '../core/rendering/RenderingPreferences';
import { makeRoundedCylinderBacksideGeometry, makeRoundedRectangleBacksideGeometry } from '../services/creation/ObjectCreation';
const geometryCache = new Map();
const materialCache = new Map();
function sphereKey(size, gp) {
    var _a, _b, _c, _d;
    const p = (gp && gp.shape === 'sphere') ? gp : undefined;
    const geodesic = (_a = p === null || p === void 0 ? void 0 : p.geodesic) !== null && _a !== void 0 ? _a : RenderingPreferences.isGeodesicEnabled();
    if (geodesic) {
        const detail = (_b = p === null || p === void 0 ? void 0 : p.icosaDetail) !== null && _b !== void 0 ? _b : 5;
        return { key: `sphere::${size}::ico${detail}`, factory: () => new THREE.IcosahedronGeometry(size, detail) };
    }
    const ws = (_c = p === null || p === void 0 ? void 0 : p.widthSegments) !== null && _c !== void 0 ? _c : 72;
    const hs = (_d = p === null || p === void 0 ? void 0 : p.heightSegments) !== null && _d !== void 0 ? _d : 64;
    return { key: `sphere::${size}::uv${ws}x${hs}`, factory: () => new THREE.SphereGeometry(size, ws, hs) };
}
function roundedCylinderKey(size, gp) {
    var _a, _b, _c, _d;
    const p = (gp && gp.shape === 'roundedCylinder') ? gp : undefined;
    const radius = size;
    const height = (_a = p === null || p === void 0 ? void 0 : p.height) !== null && _a !== void 0 ? _a : size * 1.0;
    const bevel = Math.max(1, (_b = p === null || p === void 0 ? void 0 : p.bevelRadius) !== null && _b !== void 0 ? _b : size * 0.12);
    const radial = (_c = p === null || p === void 0 ? void 0 : p.radialSegments) !== null && _c !== void 0 ? _c : 64;
    const bevelSegs = (_d = p === null || p === void 0 ? void 0 : p.bevelSegments) !== null && _d !== void 0 ? _d : 16;
    const key = `roundedCylinder::r${radius}::h${height}::b${bevel}::rs${radial}::bs${bevelSegs}`;
    return { key, factory: () => makeRoundedCylinderBacksideGeometry(radius, height, bevel, radial, bevelSegs) };
}
function roundedRectangleKey(size, gp) {
    var _a, _b, _c, _d, _e;
    const p = (gp && gp.shape === 'roundedRectangle') ? gp : undefined;
    const length = (_a = p === null || p === void 0 ? void 0 : p.length) !== null && _a !== void 0 ? _a : size * 2.0;
    const width = (_b = p === null || p === void 0 ? void 0 : p.width) !== null && _b !== void 0 ? _b : size * 1.2;
    const height = (_c = p === null || p === void 0 ? void 0 : p.height) !== null && _c !== void 0 ? _c : size * 1.0;
    const corner = Math.max(1, (_d = p === null || p === void 0 ? void 0 : p.cornerRadius) !== null && _d !== void 0 ? _d : size * 0.18);
    const bevelSegs = (_e = p === null || p === void 0 ? void 0 : p.bevelSegments) !== null && _e !== void 0 ? _e : 16;
    const key = `roundedRectangle::L${length}::W${width}::H${height}::R${corner}::bs${bevelSegs}`;
    return { key, factory: () => makeRoundedRectangleBacksideGeometry(length, width, height, corner, bevelSegs) };
}
function geometrySignature(shape, size, gp) {
    switch (shape) {
        case 'sphere': return sphereKey(size, gp);
        case 'tetrahedron': return { key: `tetra::${size}`, factory: () => new THREE.TetrahedronGeometry(size) };
        case 'cube': return { key: `cube::${size}`, factory: () => new THREE.BoxGeometry(size, size, size) };
        case 'octahedron': return { key: `octa::${size}`, factory: () => new THREE.OctahedronGeometry(size) };
        case 'dodecahedron': return { key: `dodeca::${size}`, factory: () => new THREE.DodecahedronGeometry(size) };
        case 'icosahedron': return { key: `icosa::${size}`, factory: () => new THREE.IcosahedronGeometry(size) };
        case 'roundedCylinder': return roundedCylinderKey(size, gp);
        case 'roundedRectangle': return roundedRectangleKey(size, gp);
        default: return sphereKey(size, gp);
    }
}
function materialKey(options) {
    const color = new THREE.Color(options.color).getHexString();
    return [
        color,
        options.side,
        options.transparent ? 1 : 0,
        options.opacity.toFixed(4),
        options.depthTest ? 1 : 0,
        options.depthWrite ? 1 : 0,
        options.wireframe ? 1 : 0,
    ].join('::');
}
export function acquireGeometry(shape, size, gp) {
    const { key, factory } = geometrySignature(shape, size, gp);
    const cached = geometryCache.get(key);
    if (cached) {
        cached.refCount += 1;
        return { geometry: cached.geometry, key };
    }
    const geometry = factory();
    geometryCache.set(key, { geometry, refCount: 1 });
    return { geometry, key };
}
export function acquireMaterial(options) {
    const key = materialKey({
        color: options.color,
        side: options.side,
        transparent: options.transparent,
        opacity: options.opacity,
        depthTest: options.depthTest,
        depthWrite: options.depthWrite,
        wireframe: !!options.wireframe,
    });
    const cached = materialCache.get(key);
    if (cached) {
        cached.refCount += 1;
        return { material: cached.material, key };
    }
    const material = new THREE.MeshBasicMaterial({
        color: options.color,
        side: options.side,
        transparent: options.transparent,
        opacity: options.opacity,
        depthTest: options.depthTest,
        depthWrite: options.depthWrite,
        wireframe: !!options.wireframe,
    });
    materialCache.set(key, { material, refCount: 1 });
    return { material, key };
}
export function releaseGeometry(key) {
    const cached = geometryCache.get(key);
    if (!cached)
        return;
    cached.refCount -= 1;
    if (cached.refCount <= 0) {
        cached.geometry.dispose();
        geometryCache.delete(key);
    }
}
export function releaseMaterial(key) {
    const cached = materialCache.get(key);
    if (!cached)
        return;
    cached.refCount -= 1;
    if (cached.refCount <= 0) {
        cached.material.dispose();
        materialCache.delete(key);
    }
}
export function debugSnapshot() {
    return {
        geometries: Array.from(geometryCache.entries()).map(([key, entry]) => ({ key, refs: entry.refCount })),
        materials: Array.from(materialCache.entries()).map(([key, entry]) => ({ key, refs: entry.refCount })),
    };
}
